import { OverlayContext } from "@/components/modal/overlay-context";
import type { OverlayFocusScopeProps } from "@/components/modal/overlay.types";
import { createFocusTrap, type FocusTrap } from "focus-trap";
import React, { useContext, useLayoutEffect, useRef } from "react";

export const OverlayFocusScope: React.FC<OverlayFocusScopeProps> = ({
  active,
  blocking,
  containers,
  element,
}) => {
  const { focusTrapStack } = useContext(OverlayContext)!;
  const opener = useRef(document.activeElement as HTMLElement | null);
  const trapRef = useRef<FocusTrap | null>(null);

  useLayoutEffect(() => {
    if (!element || !blocking) return;
    const dialogContainer = () =>
      element.querySelector<HTMLElement>('[role="dialog"][tabindex]') ?? element;
    const trap = createFocusTrap(element, {
      trapStack: focusTrapStack,
      escapeDeactivates: false,
      allowOutsideClick: true,
      preventScroll: true,
      initialFocus: () =>
        element.querySelector<HTMLElement>("[data-autofocus], [autofocus]") ??
        // React autoFocus runs before the trap activates and does not set an HTML attribute.
        (element.contains(document.activeElement) ? undefined : dialogContainer()),
      fallbackFocus: dialogContainer,
      setReturnFocus: () => {
        // Strict Mode's replacement trap owns focus; stale cleanup must not move it.
        if (trapRef.current || !opener.current?.isConnected) return false;
        return opener.current;
      },
    });
    trapRef.current = trap;
    return () => {
      trapRef.current = null;
      trap.deactivate();
    };
  }, [element, blocking, focusTrapStack]);

  useLayoutEffect(() => {
    const trap = trapRef.current;
    if (!trap) return;
    if (active) {
      // Nonblocking layers above the modal remain part of its keyboard-accessible boundary.
      trap.updateContainerElements(containers);
      if (trap.active) trap.unpause();
      else trap.activate();
    } else if (trap.active) {
      trap.pause();
    }
  }, [active, containers, element, blocking]);

  return null;
};
